/**
 * Instagram Login OAuth, in a popup.
 *
 * The "Instagram API with Instagram Login" product has no JavaScript SDK — the
 * only documented flow is a redirect to instagram.com that comes back with a
 * `code`. Doing that as a full-page navigation would drop the user out of
 * Settings, so we run it in a popup and keep the SDK-like feel of the other
 * channels: the popup posts the code back to the opener and closes itself.
 *
 * `handleInstagramOAuthPopupCallback` runs before React mounts (see main.tsx)
 * so the popup never pays for a full app boot.
 */

import { INSTAGRAM_APP_ID, INSTAGRAM_LOGIN_OAUTH_SCOPES } from '@/config/env'

const POPUP_MESSAGE_TYPE = 'rk:instagram-oauth'
const STATE_PREFIX = 'ig_login'
/** Instagram's consent screen can involve a login and a couple of steps. */
const POPUP_TIMEOUT_MS = 5 * 60 * 1000
const POPUP_WIDTH = 620
const POPUP_HEIGHT = 760

export class InstagramOAuthError extends Error {
  /** True when the browser blocked the popup, so the caller can fall back. */
  public popupBlocked: boolean
  /** True when the user closed the window without authorising. */
  public cancelled: boolean

  constructor(message: string, options: { popupBlocked?: boolean; cancelled?: boolean } = {}) {
    super(message)
    this.name = 'InstagramOAuthError'
    this.popupBlocked = options.popupBlocked ?? false
    this.cancelled = options.cancelled ?? false
  }
}

interface InstagramOAuthMessage {
  type: typeof POPUP_MESSAGE_TYPE
  state: string
  code?: string
  error?: string
}

function buildState(): string {
  const random = Math.random().toString(36).slice(2)
  return `${STATE_PREFIX}_${Date.now().toString(36)}_${random}`
}

function buildAuthorizeUrl(redirectUri: string, state: string): string {
  const params = new URLSearchParams({
    client_id: INSTAGRAM_APP_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: INSTAGRAM_LOGIN_OAUTH_SCOPES.join(','),
    state,
  })
  return `https://www.instagram.com/oauth/authorize?${params.toString()}`
}

/**
 * Called at boot. When this document is the OAuth popup, hand the result to the
 * opener and close, returning true so the caller can skip rendering.
 */
export function handleInstagramOAuthPopupCallback(): boolean {
  if (typeof window === 'undefined' || !window.opener || window.opener === window) {
    return false
  }

  const params = new URLSearchParams(window.location.search)
  const state = params.get('state') || ''
  if (!state.startsWith(STATE_PREFIX)) {
    // Some other flow's popup (or none at all) — leave it alone.
    return false
  }

  const message: InstagramOAuthMessage = {
    type: POPUP_MESSAGE_TYPE,
    state,
    code: params.get('code') || undefined,
    error:
      params.get('error_description') ||
      params.get('error_reason') ||
      params.get('error') ||
      undefined,
  }

  try {
    window.opener.postMessage(message, window.location.origin)
  } catch {
    // Opener is gone or cross-origin; nothing to hand back.
    return false
  }

  window.close()
  return true
}

class InstagramOAuthService {
  isConfigured(): boolean {
    return Boolean(INSTAGRAM_APP_ID)
  }

  /**
   * Open the consent popup and resolve with the authorization `code`.
   *
   * @param redirectUri must match one of the app's registered OAuth redirect
   *   URIs exactly, or Instagram answers "Invalid redirect_uri".
   */
  async login(redirectUri: string): Promise<string> {
    if (!this.isConfigured()) {
      throw new InstagramOAuthError('Instagram App ID is not configured in this environment')
    }

    const state = buildState()
    const url = buildAuthorizeUrl(redirectUri, state)

    const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - POPUP_WIDTH) / 2))
    const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2))
    const popup = window.open(
      url,
      'rk-instagram-oauth',
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    )

    if (!popup) {
      throw new InstagramOAuthError(
        'The browser blocked the Instagram window. Allow pop-ups for this site and try again.',
        { popupBlocked: true },
      )
    }

    return new Promise<string>((resolve, reject) => {
      let settled = false

      const cleanup = () => {
        window.removeEventListener('message', onMessage)
        window.clearInterval(closedTimer)
        window.clearTimeout(timeoutTimer)
      }

      const finish = (fn: () => void) => {
        if (settled) return
        settled = true
        cleanup()
        fn()
      }

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return
        const data = event.data as InstagramOAuthMessage | undefined
        if (!data || data.type !== POPUP_MESSAGE_TYPE || data.state !== state) return

        if (data.code) {
          finish(() => resolve(data.code as string))
          return
        }
        finish(() =>
          reject(
            new InstagramOAuthError(
              data.error || 'Instagram authorization was not completed.',
            ),
          ),
        )
      }

      // A closed popup is the only signal we get when the user gives up: the
      // consent page is cross-origin, so nothing else is observable.
      const closedTimer = window.setInterval(() => {
        if (popup.closed) {
          finish(() =>
            reject(
              new InstagramOAuthError('Instagram authorization was cancelled.', {
                cancelled: true,
              }),
            ),
          )
        }
      }, 500)

      const timeoutTimer = window.setTimeout(() => {
        try {
          popup.close()
        } catch {
          /* already gone */
        }
        finish(() => reject(new InstagramOAuthError('Instagram authorization timed out.')))
      }, POPUP_TIMEOUT_MS)

      window.addEventListener('message', onMessage)
    })
  }
}

export const instagramOAuthService = new InstagramOAuthService()
